import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { printHtmlSafely } from "@/lib/printHtml";
import {
  Search, Store, Package, AlertTriangle, Calendar, ChevronLeft, ChevronRight,
  ArrowUpDown, Download, FileSpreadsheet, FileText, Filter, X, ChevronDown,
  Ban, Clock, CheckCircle2, Info, Trash2, Activity,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StockDiagnosticDialog from "./StockDiagnosticDialog";

// Get expiry status color and indicator
const getExpiryIndicator = (status, days) => {
  if (status === "expired") return { color: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500", label: "Expired" };
  if (days !== null && days <= 30) return { color: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500", label: `${days}d` };
  if (days !== null && days <= 60) return { color: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500", label: `${days}d` };
  if (days !== null && days <= 90) return { color: "bg-yellow-50 text-yellow-700 border-yellow-200", dot: "bg-yellow-500", label: `${days}d` };
  if (days !== null) return { color: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", label: `${days}d` };
  return { color: "bg-gray-100 text-gray-500 border-gray-200", dot: "bg-gray-400", label: "N/A" };
};

// Multi-select dropdown component
function MultiSelectFilter({ label, options, selected, onSelect, placeholder, icon: Icon }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  
  const filtered = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase())
  );
  
  const toggle = (value) => {
    if (selected.includes(value)) {
      onSelect(selected.filter(v => v !== value));
    } else {
      onSelect([...selected, value]);
    }
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 justify-between min-w-[120px]">
          <span className="flex items-center gap-1 truncate">
            {Icon && <Icon className="w-3 h-3" />}
            {selected.length === 0 ? placeholder : `${selected.length} selected`}
          </span>
          <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-2" align="start">
        <Input
          placeholder={`Search ${label.toLowerCase()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 mb-2"
        />
        <div className="max-h-[200px] overflow-y-auto space-y-1">
          {filtered.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-2">No results</div>
          ) : filtered.slice(0, 50).map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
            >
              <Checkbox
                checked={selected.includes(opt)}
                onCheckedChange={() => toggle(opt)}
              />
              <span className="truncate">{opt}</span>
            </label>
          ))}
          {filtered.length > 50 && (
            <div className="text-xs text-muted-foreground text-center py-1">
              +{filtered.length - 50} more
            </div>
          )}
        </div>
        {selected.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 h-7 text-xs"
            onClick={() => { onSelect([]); setOpen(false); }}
          >
            Clear selection
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function ShopWiseStockView({ shops }) {
  const { user } = useAuth();
  const isMasterOrSuper = user?.role === "master" || user?.role === "super_user";
  
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filter options
  const [brandOptions, setBrandOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  
  // Filters - default to first shop if available
  const [selectedShop, setSelectedShop] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("expiry_date");
  const [sortDir, setSortDir] = useState("asc");
  const [filterType, setFilterType] = useState("");
  const [expiryRange, setExpiryRange] = useState("");
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [stockStatus, setStockStatus] = useState("");
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  // Default to first shop when shops become available
  useEffect(() => {
    if (shops && shops.length > 0 && !selectedShop) {
      setSelectedShop(shops[0].id);
    }
  }, [shops, selectedShop, setSelectedShop]);
  
  // Don't fetch until shop is initialized (has a value)
  const shopReady = selectedShop || (shops.length === 0);

  // Diagnostic modal
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);

  // Selection and deletion state (MASTER only)
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(""); // "single" | "selected" | "filtered"
  const [deleteSingleBatch, setDeleteSingleBatch] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Stock Change Diagnostic dialog (MASTER only)
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [selectedShop, searchQuery, filterType, expiryRange, selectedBrands, selectedGroups, stockStatus, page]);

  const openDiagnostic = useCallback(async () => {
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagData(null);
    try {
      const { data } = await api.get("/stock/shop-view/diagnostic", {
        params: { shop_id: selectedShop || "" },
      });
      setDiagData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDiagLoading(false);
    }
  }, [selectedShop]);

  // Fetch brand and group options
  useEffect(() => {
    api.get("/products/filters").then(({ data }) => {
      setBrandOptions((data.brands || []).map(b => b.name).filter(Boolean));
      setGroupOptions((data.groups || []).map(g => g.name).filter(Boolean));
    }).catch(() => {});
  }, []);

  const buildParams = useCallback(() => ({
    shop_id: selectedShop,
    q: searchQuery,
    sort_by: sortBy,
    sort_dir: sortDir,
    filter_type: filterType,
    expiry_range: expiryRange,
    brands: selectedBrands.join(","),
    groups: selectedGroups.join(","),
    stock_status: stockStatus,
  }), [selectedShop, searchQuery, sortBy, sortDir, filterType, expiryRange, selectedBrands, selectedGroups, stockStatus]);

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...buildParams(),
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      const { data } = await api.get("/stock/shop-view", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, pageSize]);

  useEffect(() => {
    // Only fetch when shop is ready (either selected or no shops available)
    if (shopReady) {
      fetchStock();
    }
  }, [fetchStock, shopReady]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedShop, searchQuery, sortBy, sortDir, filterType, expiryRange, selectedBrands, selectedGroups, stockStatus]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const clearAllFilters = () => {
    // Do NOT reset selectedShop - it's the primary filter context
    // Only reset secondary filters
    setSearchQuery("");
    setFilterType("");
    setExpiryRange("");
    setSelectedBrands([]);
    setSelectedGroups([]);
    setStockStatus("");
  };

  // hasActiveFilters should check for secondary filters (not including shop)
  // Shop is the primary context and doesn't count as a "filter to clear"
  const hasSecondaryFilters = searchQuery || filterType || expiryRange || 
    selectedBrands.length > 0 || selectedGroups.length > 0 || stockStatus;
  
  // For showing the filter tags section, include shop selection
  const hasActiveFilters = selectedShop || hasSecondaryFilters;

  // Selection helpers (MASTER only)
  const toggleRowSelection = (stockId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stockId)) {
        newSet.delete(stockId);
      } else {
        newSet.add(stockId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === items.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(items.map(item => item.id)));
    }
  };

  const isAllSelected = items.length > 0 && selectedRows.size === items.length;
  const isSomeSelected = selectedRows.size > 0 && selectedRows.size < items.length;

  // Delete handlers
  const handleDeleteSingle = (item) => {
    setDeleteSingleBatch(item);
    setDeleteMode("single");
    setDeleteDialogOpen(true);
  };

  const handleDeleteSelected = () => {
    if (selectedRows.size === 0) return;
    setDeleteMode("selected");
    setDeleteDialogOpen(true);
  };

  const handleDeleteFiltered = () => {
    if (!selectedShop) {
      toast.error("Please select a shop first");
      return;
    }
    setDeleteMode("filtered");
    setDeleteDialogOpen(true);
  };

  const executeDelete = async () => {
    setDeleting(true);
    try {
      if (deleteMode === "single" && deleteSingleBatch) {
        // Use item.id as the stock batch identifier
        await api.delete(`/stock/batch/${deleteSingleBatch.id}`);
        toast.success("Stock batch deleted");
      } else if (deleteMode === "selected") {
        // selectedRows contains stock IDs (item.id)
        const stockIds = Array.from(selectedRows);
        const { data } = await api.post("/stock/bulk-delete", { 
          batch_ids: stockIds,
          shop_id: selectedShop || null
        });
        toast.success(`Deleted ${data.deleted_count} stock batches (${data.total_quantity_removed} units)`);
      } else if (deleteMode === "filtered") {
        const { data } = await api.post("/stock/delete-filtered", {
          shop_id: selectedShop,
          search_query: searchQuery || null,
          filter_type: filterType || null,
          expiry_range: expiryRange || null,
          brands: selectedBrands.length > 0 ? selectedBrands : null,
          groups: selectedGroups.length > 0 ? selectedGroups : null,
          stock_status: stockStatus || null,
        });
        toast.success(`Deleted ${data.deleted_count} stock batches (${data.total_quantity_removed} units)`);
      }
      
      // Reset state and refresh
      setDeleteDialogOpen(false);
      setDeleteSingleBatch(null);
      setSelectedRows(new Set());
      fetchStock();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  // Get deletion summary for confirmation dialog
  const getDeleteSummary = () => {
    if (deleteMode === "single" && deleteSingleBatch) {
      return {
        count: 1,
        quantity: deleteSingleBatch.quantity || 0,
        productCount: 1,
        shopName: shops.find(s => s.id === deleteSingleBatch.shop_id)?.name || "",
      };
    } else if (deleteMode === "selected") {
      // Use item.id for matching since selectedRows contains stock IDs
      const selectedItems = items.filter(item => selectedRows.has(item.id));
      const quantity = selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const productIds = new Set(selectedItems.map(item => item.product_id));
      return {
        count: selectedRows.size,
        quantity,
        productCount: productIds.size,
        shopName: selectedShop ? shops.find(s => s.id === selectedShop)?.name : "Multiple",
      };
    } else if (deleteMode === "filtered") {
      // For filtered, we use the total from current view
      const quantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const productIds = new Set(items.map(item => item.product_id));
      return {
        count: total, // Total matching the filter
        quantity: summary?.total_quantity || quantity,
        productCount: summary?.unique_products || productIds.size,
        shopName: shops.find(s => s.id === selectedShop)?.name || "",
      };
    }
    return { count: 0, quantity: 0, productCount: 0, shopName: "" };
  };

  // Export functions
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      const { data } = await api.get("/stock/shop-view/export", { params });
      
      if (!data.items || data.items.length === 0) {
        toast.error("No data to export");
        return;
      }

      const shopName = selectedShop ? shops.find(s => s.id === selectedShop)?.name || "Shop" : "AllShops";
      const reportDate = new Date().toISOString().slice(0,10);
      
      // Build Excel-friendly HTML with landscape print settings
      const excelContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head>
          <meta charset="UTF-8">
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Stock Report</x:Name>
                  <x:WorksheetOptions>
                    <x:Print>
                      <x:ValidPrinterInfo/>
                      <x:PaperSizeIndex>9</x:PaperSizeIndex>
                      <x:HorizontalResolution>600</x:HorizontalResolution>
                      <x:VerticalResolution>600</x:VerticalResolution>
                    </x:Print>
                    <x:PageSetup>
                      <x:Layout>
                        <x:Orientation>Landscape</x:Orientation>
                      </x:Layout>
                      <x:Header x:Margin="0.3"/>
                      <x:Footer x:Margin="0.3"/>
                      <x:PageMargins x:Bottom="0.5" x:Left="0.5" x:Right="0.5" x:Top="0.5"/>
                    </x:PageSetup>
                    <x:FitToPage/>
                    <x:Print>
                      <x:FitWidth>1</x:FitWidth>
                      <x:FitHeight>0</x:FitHeight>
                    </x:Print>
                  </x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
          <style>
            table { border-collapse: collapse; width: 100%; }
            th { background-color: #9E6D5B; color: white; font-weight: bold; text-align: left; padding: 8px; border: 1px solid #666; }
            td { padding: 6px 8px; border: 1px solid #ddd; vertical-align: top; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .product { min-width: 320px; white-space: nowrap; }
            .brand { min-width: 100px; }
            .group { min-width: 100px; }
            .shop { min-width: 70px; }
            .batch { min-width: 80px; }
            .expiry { min-width: 90px; }
            .days { min-width: 50px; text-align: right; }
            .qty { min-width: 50px; text-align: right; }
            .mrp { min-width: 60px; text-align: right; }
            .expired { color: #dc2626; font-weight: bold; }
            .low-stock { color: #dc2626; font-weight: bold; }
            .header-row td { font-weight: bold; background: #f5f5f5; border: none; }
            .title { font-size: 18px; color: #9E6D5B; }
            .subtitle { font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <table>
            <tr class="header-row">
              <td colspan="9" class="title">STOCK REPORT - ${shopName}</td>
            </tr>
            <tr class="header-row">
              <td colspan="9" class="subtitle">Generated: ${formatDateTime(new Date().toISOString())} | Total: ${data.total} batches | ${data.summary.total_products} products | ${data.summary.total_quantity.toLocaleString()} units</td>
            </tr>
            <tr><td colspan="9" style="height:10px; border:none;"></td></tr>
            <tr>
              <th class="product">Product Name</th>
              <th class="brand">Brand</th>
              <th class="group">Group</th>
              <th class="shop">Shop</th>
              <th class="batch">Barcode</th>
              <th class="expiry">Expiry Date</th>
              <th class="days">Days</th>
              <th class="qty">Qty</th>
              <th class="mrp">MRP</th>
            </tr>
            ${data.items.map(item => {
              const daysClass = item.expiry_status === "expired" ? "expired" : "";
              const qtyClass = item.quantity <= 5 ? "low-stock" : "";
              return `
                <tr>
                  <td class="product">${item.product_name || ""}</td>
                  <td class="brand">${item.brand_name || "—"}</td>
                  <td class="group">${item.group_name || "—"}</td>
                  <td class="shop">${item.shop_name || "—"}</td>
                  <td class="batch">${item.auto_barcode || "—"}</td>
                  <td class="expiry">${item.expiry_date || "N/A"}</td>
                  <td class="days ${daysClass}">${item.days_until_expiry !== null ? item.days_until_expiry : "N/A"}</td>
                  <td class="qty ${qtyClass}">${item.quantity}</td>
                  <td class="mrp">${item.mrp !== null ? "₹" + Number(item.mrp).toFixed(0) : "N/A"}</td>
                </tr>
              `;
            }).join("")}
          </table>
        </body>
        </html>
      `;

      const blob = new Blob(["\ufeff" + excelContent], { type: "application/vnd.ms-excel;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Stock_Report_${shopName}_${reportDate}.xls`;
      link.click();
      
      toast.success(`Exported ${data.items.length} records (A4 Landscape)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      const { data } = await api.get("/stock/shop-view/export", { params });
      
      if (!data.items || data.items.length === 0) {
        toast.error("No data to export");
        return;
      }

      const shopName = selectedShop ? shops.find(s => s.id === selectedShop)?.name || "All Shops" : "All Shops";
      
      // Build filters string
      const filterStrings = [];
      if (data.filters_applied?.shop) filterStrings.push(`Shop: ${data.filters_applied.shop}`);
      if (data.filters_applied?.search) filterStrings.push(`Search: ${data.filters_applied.search}`);
      if (data.filters_applied?.brands) filterStrings.push(`Brands: ${data.filters_applied.brands}`);
      if (data.filters_applied?.groups) filterStrings.push(`Groups: ${data.filters_applied.groups}`);
      if (data.filters_applied?.expiry) filterStrings.push(`Expiry: ${data.filters_applied.expiry}`);
      if (data.filters_applied?.stock_status) filterStrings.push(`Stock: ${data.filters_applied.stock_status}`);
      
      // Generate printable HTML - A4 LANDSCAPE with full product names
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Stock Report - ${shopName}</title>
          <style>
            @page { 
              size: A4 landscape; 
              margin: 8mm 10mm; 
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              font-size: 8px; 
              line-height: 1.2; 
              color: #333; 
              width: 100%;
            }
            .header { 
              text-align: center; 
              margin-bottom: 8px; 
              border-bottom: 2px solid #9E6D5B; 
              padding-bottom: 6px; 
            }
            .header h1 { 
              font-size: 14px; 
              color: #9E6D5B; 
              margin-bottom: 2px; 
              letter-spacing: 1px;
            }
            .header .subtitle { font-size: 10px; color: #666; }
            .meta { 
              display: flex; 
              justify-content: space-between; 
              margin-bottom: 6px; 
              font-size: 7px; 
              color: #666; 
            }
            .filters { 
              background: #f5f5f5; 
              padding: 4px 6px; 
              border-radius: 3px; 
              margin-bottom: 6px; 
              font-size: 7px; 
            }
            .summary { 
              display: flex; 
              gap: 10px; 
              margin-bottom: 8px; 
            }
            .summary-item { 
              background: #f9f9f9; 
              padding: 4px 8px; 
              border-radius: 3px; 
              text-align: center; 
              border: 1px solid #eee;
            }
            .summary-item .value { font-size: 12px; font-weight: bold; color: #9E6D5B; }
            .summary-item .label { font-size: 6px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; }
            
            table { 
              width: 100%; 
              border-collapse: collapse; 
              font-size: 7.5px; 
              table-layout: fixed;
            }
            th { 
              background: #9E6D5B; 
              color: white; 
              padding: 4px 3px; 
              text-align: left; 
              font-weight: 600; 
              font-size: 7px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
              border: 1px solid #8a5d4d;
            }
            td { 
              padding: 3px; 
              border-bottom: 1px solid #e5e5e5; 
              border-left: 1px solid #f0f0f0;
              border-right: 1px solid #f0f0f0;
              vertical-align: middle; 
            }
            tr:nth-child(even) { background: #fafafa; }
            tr:hover { background: #f5f5f5; }
            
            /* Column widths optimized for landscape - Product gets maximum space */
            .col-product { width: 32%; text-align: left; }
            .col-brand { width: 10%; text-align: left; }
            .col-group { width: 10%; text-align: left; }
            .col-shop { width: 7%; text-align: left; }
            .col-batch { width: 9%; text-align: left; }
            .col-expiry { width: 9%; text-align: center; }
            .col-days { width: 6%; text-align: right; }
            .col-qty { width: 5%; text-align: right; }
            .col-mrp { width: 6%; text-align: right; }
            
            /* Product name - full display, no truncation */
            .product-name { 
              font-weight: 500; 
              white-space: nowrap;
              overflow: visible;
              font-size: 7px;
            }
            .barcode { 
              font-family: 'Consolas', monospace; 
              font-size: 6.5px; 
              color: #666; 
            }
            .brand-name { font-size: 7px; color: #555; }
            .group-name { font-size: 7px; color: #9E6D5B; font-weight: 500; }
            .shop-name { font-size: 7px; }
            .expiry-date { font-family: monospace; font-size: 7px; }
            .days-left { font-family: monospace; font-weight: 600; font-size: 7px; }
            .qty-value { font-family: monospace; font-weight: 600; font-size: 7px; }
            .mrp-value { font-family: monospace; font-size: 7px; }
            
            .expired { color: #dc2626; font-weight: bold; }
            .critical { color: #ea580c; font-weight: bold; }
            .warning { color: #ca8a04; }
            .low-stock { color: #dc2626; font-weight: bold; }
            
            .footer { 
              margin-top: 8px; 
              text-align: center; 
              font-size: 6px; 
              color: #999; 
              border-top: 1px solid #eee; 
              padding-top: 4px; 
            }
            
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              tr { page-break-inside: avoid; }
              thead { display: table-header-group; }
              tfoot { display: table-footer-group; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>STOCK REPORT</h1>
            <div class="subtitle">${shopName}</div>
          </div>
          
          <div class="meta">
            <span>Generated: ${formatDateTime(new Date().toISOString())}</span>
            <span>Total: ${data.total} batches | ${data.summary.total_products} products | ${data.summary.total_quantity.toLocaleString()} units</span>
          </div>
          
          ${filterStrings.length > 0 ? `<div class="filters"><strong>Filters:</strong> ${filterStrings.join(" | ")}</div>` : ""}
          
          <div class="summary">
            <div class="summary-item">
              <div class="value">${data.summary.total_products}</div>
              <div class="label">Products</div>
            </div>
            <div class="summary-item">
              <div class="value">${data.summary.total_quantity.toLocaleString()}</div>
              <div class="label">Total Qty</div>
            </div>
            <div class="summary-item">
              <div class="value" style="color:#dc2626">${data.summary.expiry_breakdown?.expired || 0}</div>
              <div class="label">Expired</div>
            </div>
            <div class="summary-item">
              <div class="value" style="color:#ea580c">${data.summary.expiry_breakdown?.within_30 || 0}</div>
              <div class="label">≤30 Days</div>
            </div>
            <div class="summary-item">
              <div class="value" style="color:#ca8a04">${data.summary.expiry_breakdown?.within_60 || 0}</div>
              <div class="label">31-60d</div>
            </div>
            <div class="summary-item">
              <div class="value" style="color:#65a30d">${(data.summary.expiry_breakdown?.beyond_90 || 0) + (data.summary.expiry_breakdown?.no_expiry || 0)}</div>
              <div class="label">&gt;90d</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th class="col-product">Product</th>
                <th class="col-brand">Brand</th>
                <th class="col-group">Group</th>
                <th class="col-shop">Shop</th>
                <th class="col-batch">Barcode</th>
                <th class="col-expiry">Expiry</th>
                <th class="col-days">Days</th>
                <th class="col-qty">Qty</th>
                <th class="col-mrp">MRP</th>
              </tr>
            </thead>
            <tbody>
              ${data.items.map(item => {
                const daysClass = item.expiry_status === "expired" ? "expired" : 
                  item.days_until_expiry !== null && item.days_until_expiry <= 30 ? "critical" :
                  item.days_until_expiry !== null && item.days_until_expiry <= 60 ? "warning" : "";
                const qtyClass = item.quantity <= 5 ? "low-stock" : "";
                return `
                  <tr>
                    <td class="col-product"><span class="product-name">${item.product_name}</span></td>
                    <td class="col-brand"><span class="brand-name">${item.brand_name || "—"}</span></td>
                    <td class="col-group"><span class="group-name">${item.group_name || "—"}</span></td>
                    <td class="col-shop"><span class="shop-name">${item.shop_name || "—"}</span></td>
                    <td class="col-batch"><span class="barcode">${item.auto_barcode || "—"}</span></td>
                    <td class="col-expiry"><span class="expiry-date">${item.expiry_date || "N/A"}</span></td>
                    <td class="col-days"><span class="days-left ${daysClass}">${item.days_until_expiry !== null ? item.days_until_expiry : "N/A"}</span></td>
                    <td class="col-qty"><span class="qty-value ${qtyClass}">${item.quantity}</span></td>
                    <td class="col-mrp"><span class="mrp-value">${item.mrp !== null ? "₹" + Number(item.mrp).toFixed(0) : "—"}</span></td>
                  </tr>
                `;
              }).join("")}
            </tbody>
          </table>
          
          <div class="footer">
            DYYNIT INTEL HITMAN | Stock Report - ${shopName} | Generated ${formatDate(new Date().toISOString())}
          </div>
        </body>
        </html>
      `;

      // Open print dialog using a sandboxed iframe (avoids document.write XSS)
      printHtmlSafely(printContent, {
        onError: (err) => {
          console.error("Print failed:", err);
          toast.error("Could not open print dialog");
        },
      });
      
      toast.success("PDF report ready for printing (A4 Landscape)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="shop-wise-stock-view">
      {/* Filters Row 1: Shop, Search, Export */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Shop Selector */}
          <div className="w-full sm:w-40">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Shop</label>
            <Select value={selectedShop || (isMasterOrSuper ? "all" : "")} onValueChange={(v) => setSelectedShop(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="shop-filter-select">
                <SelectValue placeholder={isMasterOrSuper ? "All Shops" : "Select Shop"} />
              </SelectTrigger>
              <SelectContent>
                {isMasterOrSuper && (
                  <SelectItem value="all">All Shops</SelectItem>
                )}
                {shops.map((shop) => (
                  <SelectItem key={shop.id} value={shop.id}>{shop.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[180px]">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-9 h-9"
                placeholder="Search by barcode, product, group or brand..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="stock-view-search"
              />
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              disabled={exporting || total === 0}
              className="h-9"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToPDF}
              disabled={exporting || total === 0}
              className="h-9"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
            
            {/* Stock Diagnostic - MASTER only */}
            {isMasterOrSuper && (
              <Button
                variant="outline"
                onClick={() => setDiagnosticOpen(true)}
                className="h-9 border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="stock-diagnostic-btn"
              >
                <Activity className="w-4 h-4 mr-1" />
                Diagnostic
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Filters Row 2: Advanced Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground mr-2">Filters:</span>
          
          {/* Brand Multi-Select */}
          <MultiSelectFilter
            label="Brand"
            options={brandOptions}
            selected={selectedBrands}
            onSelect={setSelectedBrands}
            placeholder="Brand"
            icon={Package}
          />
          
          {/* Group Multi-Select */}
          <MultiSelectFilter
            label="Group"
            options={groupOptions}
            selected={selectedGroups}
            onSelect={setSelectedGroups}
            placeholder="Group"
          />
          
          {/* Expiry Range Filter */}
          <Select value={expiryRange || "all"} onValueChange={(v) => setExpiryRange(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[130px]" data-testid="expiry-range-select">
              <Calendar className="w-3 h-3 mr-1" />
              <SelectValue placeholder="Expiry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Expiry</SelectItem>
              <SelectItem value="expired">
                <span className="flex items-center gap-1"><Ban className="w-3 h-3 text-red-500" /> Expired</span>
              </SelectItem>
              <SelectItem value="30">≤30 Days</SelectItem>
              <SelectItem value="60">31-60 Days</SelectItem>
              <SelectItem value="90">61-90 Days</SelectItem>
              <SelectItem value="180">&gt;90 Days</SelectItem>
              <SelectItem value="no_expiry">No Expiry</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Stock Status Filter */}
          <Select value={stockStatus || "all"} onValueChange={(v) => setStockStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[130px]" data-testid="stock-status-select">
              <SelectValue placeholder="Stock Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stock</SelectItem>
              <SelectItem value="in_stock">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> In Stock</span>
              </SelectItem>
              <SelectItem value="low_stock">
                <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-orange-500" /> Low Stock</span>
              </SelectItem>
              <SelectItem value="zero_stock">
                <span className="flex items-center gap-1"><Ban className="w-3 h-3 text-red-500" /> Zero Stock</span>
              </SelectItem>
            </SelectContent>
          </Select>
          
          {/* Quick Filter Buttons */}
          <Button
            variant={filterType === "low_stock" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterType(filterType === "low_stock" ? "" : "low_stock")}
            className="h-9 text-xs"
            style={filterType === "low_stock" ? { backgroundColor: "#9E6D5B" } : {}}
            data-testid="filter-low-stock"
          >
            <AlertTriangle className="w-3 h-3 mr-1" /> Low
          </Button>
          <Button
            variant={filterType === "near_expiry" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterType(filterType === "near_expiry" ? "" : "near_expiry")}
            className="h-9 text-xs"
            style={filterType === "near_expiry" ? { backgroundColor: "#9E6D5B" } : {}}
            data-testid="filter-near-expiry"
          >
            <Clock className="w-3 h-3 mr-1" /> Expiring
          </Button>
          
          {/* Clear All Filters (only secondary filters, not shop) */}
          {hasSecondaryFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-9 text-xs text-muted-foreground"
            >
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
        
        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t">
            {selectedShop && (
              <Badge variant="secondary" className="text-[10px] gap-1">
                Shop: {shops.find(s => s.id === selectedShop)?.name}
                <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setSelectedShop("")} />
              </Badge>
            )}
            {searchQuery && (
              <Badge variant="secondary" className="text-[10px] gap-1">
                Search: {searchQuery}
                <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setSearchQuery("")} />
              </Badge>
            )}
            {selectedBrands.map(b => (
              <Badge key={b} variant="secondary" className="text-[10px] gap-1">
                Brand: {b}
                <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setSelectedBrands(selectedBrands.filter(x => x !== b))} />
              </Badge>
            ))}
            {selectedGroups.map(g => (
              <Badge key={g} variant="secondary" className="text-[10px] gap-1">
                Group: {g}
                <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setSelectedGroups(selectedGroups.filter(x => x !== g))} />
              </Badge>
            ))}
            {expiryRange && (
              <Badge variant="secondary" className="text-[10px] gap-1">
                Expiry: {expiryRange === "expired" ? "Expired" : expiryRange === "no_expiry" ? "N/A" : `≤${expiryRange}d`}
                <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setExpiryRange("")} />
              </Badge>
            )}
            {stockStatus && (
              <Badge variant="secondary" className="text-[10px] gap-1">
                {stockStatus.replace("_", " ")}
                <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setStockStatus("")} />
              </Badge>
            )}
          </div>
        )}
      </Card>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <Card className="p-2.5 text-center">
            <div className="text-lg font-bold text-foreground">{summary.total_products}</div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Products</div>
          </Card>
          <Card className="p-2.5 text-center">
            <div className="text-lg font-bold text-foreground">{(summary.total_batches ?? total).toLocaleString()}</div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Batches</div>
          </Card>
          <Card className="p-2.5 text-center relative group">
            <div className="text-lg font-bold text-foreground">{summary.total_quantity.toLocaleString()}</div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center justify-center gap-1">
              Total Qty
              <button
                type="button"
                onClick={openDiagnostic}
                className="opacity-50 hover:opacity-100 transition-opacity"
                title="Why is Total Qty different from Products? Click to audit."
                data-testid="qty-audit-btn"
              >
                <Info className="w-3 h-3" />
              </button>
            </div>
          </Card>
          {summary.expiry_breakdown && (
            <>
              <Card 
                className={`p-2.5 text-center border-red-200 bg-red-50/50 cursor-pointer hover:bg-red-100/50 transition-colors ${expiryRange === "expired" ? "ring-2 ring-red-400" : ""}`}
                onClick={() => setExpiryRange(expiryRange === "expired" ? "" : "expired")}
              >
                <div className="text-lg font-bold text-red-700">{summary.expiry_breakdown.expired}</div>
                <div className="text-[9px] uppercase tracking-wider text-red-600">Expired</div>
              </Card>
              <Card 
                className={`p-2.5 text-center border-red-200 bg-red-50/30 cursor-pointer hover:bg-red-100/30 transition-colors ${expiryRange === "30" ? "ring-2 ring-red-400" : ""}`}
                onClick={() => setExpiryRange(expiryRange === "30" ? "" : "30")}
              >
                <div className="text-lg font-bold text-red-600">{summary.expiry_breakdown.within_30}</div>
                <div className="text-[9px] uppercase tracking-wider text-red-500">≤30 Days</div>
              </Card>
              <Card 
                className={`p-2.5 text-center border-orange-200 bg-orange-50/50 cursor-pointer hover:bg-orange-100/50 transition-colors ${expiryRange === "60" ? "ring-2 ring-orange-400" : ""}`}
                onClick={() => setExpiryRange(expiryRange === "60" ? "" : "60")}
              >
                <div className="text-lg font-bold text-orange-700">{summary.expiry_breakdown.within_60}</div>
                <div className="text-[9px] uppercase tracking-wider text-orange-600">31-60d</div>
              </Card>
              <Card 
                className={`p-2.5 text-center border-yellow-200 bg-yellow-50/50 cursor-pointer hover:bg-yellow-100/50 transition-colors ${expiryRange === "90" ? "ring-2 ring-yellow-400" : ""}`}
                onClick={() => setExpiryRange(expiryRange === "90" ? "" : "90")}
              >
                <div className="text-lg font-bold text-yellow-700">{summary.expiry_breakdown.within_90}</div>
                <div className="text-[9px] uppercase tracking-wider text-yellow-600">61-90d</div>
              </Card>
              <Card 
                className={`p-2.5 text-center border-emerald-200 bg-emerald-50/50 cursor-pointer hover:bg-emerald-100/50 transition-colors ${expiryRange === "180" ? "ring-2 ring-emerald-400" : ""}`}
                onClick={() => setExpiryRange(expiryRange === "180" ? "" : "180")}
              >
                <div className="text-lg font-bold text-emerald-700">{summary.expiry_breakdown.beyond_180 + (summary.expiry_breakdown.no_expiry || 0)}</div>
                <div className="text-[9px] uppercase tracking-wider text-emerald-600">&gt;90d / N/A</div>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Results Count & Sort */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {loading ? "Loading..." : `${total} batches found`}
        </span>
        <div className="flex items-center gap-2">
          <span>Sort:</span>
          <button
            className={`px-2 py-1 rounded ${sortBy === "expiry_date" ? "bg-[#9E6D5B]/10 text-[#9E6D5B]" : ""}`}
            onClick={() => toggleSort("expiry_date")}
          >
            Expiry {sortBy === "expiry_date" && <ArrowUpDown className="w-3 h-3 inline ml-1" />}
          </button>
          <button
            className={`px-2 py-1 rounded ${sortBy === "quantity" ? "bg-[#9E6D5B]/10 text-[#9E6D5B]" : ""}`}
            onClick={() => toggleSort("quantity")}
          >
            Qty {sortBy === "quantity" && <ArrowUpDown className="w-3 h-3 inline ml-1" />}
          </button>
          
          {/* Delete Filtered Results - MASTER only */}
          {isMasterOrSuper && selectedShop && hasActiveFilters && total > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeleteFiltered}
              className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 ml-2"
              data-testid="delete-filtered-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Delete All Filtered ({total})
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Action Toolbar (MASTER only) */}
      {isMasterOrSuper && selectedRows.size > 0 && (
        <Card className="p-3 bg-amber-50 border-amber-200 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-amber-800">
                {selectedRows.size} stock row{selectedRows.size !== 1 ? "s" : ""} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedRows(new Set())}
                className="h-7 text-xs"
              >
                Clear Selection
              </Button>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteSelected}
              className="h-8"
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Delete Selected
            </Button>
          </div>
        </Card>
      )}

      {/* Stock Table */}
      <Card className="overflow-hidden">
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                {isMasterOrSuper && <col style={{ width: "4%" }} />}
                <col style={{ width: isMasterOrSuper ? "32%" : "36%" }} />
                <col style={{ width: isMasterOrSuper ? "10%" : "11%" }} />
                <col style={{ width: isMasterOrSuper ? "10%" : "11%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: isMasterOrSuper ? "10%" : "11%" }} />
                <col style={{ width: isMasterOrSuper ? "8%" : "10%" }} />
                {isMasterOrSuper && <col style={{ width: "5%" }} />}
              </colgroup>
              <thead className="bg-muted/50">
                <tr>
                  {isMasterOrSuper && (
                    <th className="px-2 py-2.5 text-center">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        className={isSomeSelected ? "data-[state=checked]:bg-amber-500" : ""}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Product</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Brand</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Group</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shop</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">MRP</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qty</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Expiry</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Days</th>
                  {isMasterOrSuper && (
                    <th className="px-2 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground"></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const expiry = getExpiryIndicator(item.expiry_status, item.days_until_expiry);
                  const isSelected = selectedRows.has(item.id);
                  return (
                    <tr 
                      key={item.id || idx} 
                      className={`border-t hover:bg-muted/30 transition-colors ${isSelected ? "bg-amber-50/50" : ""}`}
                    >
                      {isMasterOrSuper && (
                        <td className="px-2 py-2.5 align-middle text-center">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRowSelection(item.id)}
                            data-testid={`row-checkbox-${item.id}`}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2.5 align-middle">
                        <div
                          className="font-semibold text-sm text-foreground leading-snug break-words"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                          title={item.product_name}
                        >
                          {item.product_name}
                        </div>
                        {item.auto_barcode && (
                          <div className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate" title={item.auto_barcode}>
                            #{item.auto_barcode}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2.5 align-middle text-xs text-muted-foreground truncate" title={item.brand_name}>
                        {item.brand_name || "—"}
                      </td>
                      <td className="px-2 py-2.5 align-middle text-xs truncate" title={item.group_name}>
                        {item.group_name ? (
                          <span className="text-[#9E6D5B] font-medium">{item.group_name}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 align-middle">
                        <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
                          <Store className="w-3 h-3 text-[#9E6D5B] shrink-0" />
                          <span className="truncate">{item.shop_name}</span>
                        </span>
                      </td>
                      <td className="px-2 py-2.5 align-middle text-right font-mono text-xs whitespace-nowrap">
                        {item.mrp !== null ? `₹${Number(item.mrp).toFixed(0)}` : <span className="text-muted-foreground">N/A</span>}
                      </td>
                      <td className={`px-2 py-2.5 align-middle text-right font-mono font-semibold whitespace-nowrap ${item.quantity <= 5 ? "text-red-600" : ""}`}>
                        {item.quantity}
                      </td>
                      <td className="px-2 py-2.5 align-middle text-xs whitespace-nowrap">
                        {item.expiry_date ? (
                          <span className="font-mono">{item.expiry_date}</span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 align-middle text-right whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${expiry.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${expiry.dot}`}></span>
                          {expiry.label}
                        </span>
                      </td>
                      {isMasterOrSuper && (
                        <td className="px-2 py-2.5 align-middle text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteSingle(item)}
                            data-testid={`delete-row-${item.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : !loading ? (
          <div className="p-10 text-center text-muted-foreground">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No stock batches found matching your filters.</p>
          </div>
        ) : null}
      </Card>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Diagnostic Dialog */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="qty-audit-dialog">
          <DialogHeader>
            <DialogTitle>Total Quantity Audit</DialogTitle>
            <DialogDescription>
              Breakdown of why <strong>Total Qty</strong> may differ from the <strong>Products</strong> count.
            </DialogDescription>
          </DialogHeader>

          {diagLoading && (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading audit data…</div>
          )}

          {diagData && !diagLoading && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-2">
                <Card className="p-3 text-center">
                  <div className="text-2xl font-bold">{diagData.summary.unique_products}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Unique Products</div>
                </Card>
                <Card className="p-3 text-center">
                  <div className="text-2xl font-bold">{diagData.summary.total_batches}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stock Rows</div>
                </Card>
                <Card className="p-3 text-center">
                  <div className="text-2xl font-bold">{diagData.summary.total_quantity}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Qty</div>
                </Card>
              </div>

              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
                {diagData.explanation}
              </div>

              {/* Products with multiple batches */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Products with multiple stock rows ({diagData.products_with_multiple_batches.length})
                </div>
                {diagData.products_with_multiple_batches.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">None</div>
                ) : (
                  <div className="border rounded max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Product</th>
                          <th className="text-center p-2">Rows</th>
                          <th className="text-right p-2">Total Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagData.products_with_multiple_batches.map((p) => (
                          <tr key={p.product_id} className="border-t">
                            <td className="p-2">
                              <div className="font-medium">{p.product_name}</div>
                              <div className="text-[10px] text-muted-foreground font-mono">#{p.auto_barcode}</div>
                            </td>
                            <td className="p-2 text-center">{p.batch_count}</td>
                            <td className="p-2 text-right font-mono font-semibold">{p.total_qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Batches with qty > 1 */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Stock rows with quantity &gt; 1 ({diagData.batches_with_qty_gt_1.length})
                </div>
                {diagData.batches_with_qty_gt_1.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">None</div>
                ) : (
                  <div className="border rounded max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Product</th>
                          <th className="text-left p-2">Shop</th>
                          <th className="text-left p-2">Expiry</th>
                          <th className="text-right p-2">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagData.batches_with_qty_gt_1.map((b) => (
                          <tr key={b.stock_id} className="border-t">
                            <td className="p-2">
                              <div className="font-medium">{b.product_name}</div>
                              <div className="text-[10px] text-muted-foreground font-mono">#{b.auto_barcode}</div>
                            </td>
                            <td className="p-2">{b.shop}</td>
                            <td className="p-2 font-mono">{b.expiry_date || "N/A"}</td>
                            <td className="p-2 text-right font-mono font-semibold">{b.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Duplicate batches */}
              {diagData.duplicate_batches.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-1">
                    ⚠ Duplicate stock rows ({diagData.duplicate_batches.length})
                    <span className="ml-2 normal-case font-normal text-[10px] text-muted-foreground">
                      Same product + shop + expiry — likely data integrity issue
                    </span>
                  </div>
                  <div className="border rounded max-h-64 overflow-y-auto border-red-200">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Product</th>
                          <th className="text-left p-2">Shop</th>
                          <th className="text-left p-2">Expiry</th>
                          <th className="text-center p-2">Copies</th>
                          <th className="text-right p-2">Total Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagData.duplicate_batches.map((d) => (
                          <tr key={`${d.auto_barcode}-${d.shop}-${d.expiry_date}`} className="border-t">
                            <td className="p-2">
                              <div className="font-medium">{d.product_name}</div>
                              <div className="text-[10px] text-muted-foreground font-mono">#{d.auto_barcode}</div>
                            </td>
                            <td className="p-2">{d.shop}</td>
                            <td className="p-2 font-mono">{d.expiry_date || "N/A"}</td>
                            <td className="p-2 text-center font-semibold text-red-700">{d.duplicate_count}</td>
                            <td className="p-2 text-right font-mono font-semibold">{d.total_qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Orphans */}
              {diagData.orphan_batches.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-1">
                    ⚠ Orphan stock rows ({diagData.orphan_batches.length})
                    <span className="ml-2 normal-case font-normal text-[10px] text-muted-foreground">
                      Stock for deleted products
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {deleteMode === "single" ? "Delete Stock Batch" : 
               deleteMode === "selected" ? "Delete Selected Stock" : 
               "Delete All Filtered Stock"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm">
                  {deleteMode === "filtered" 
                    ? "This will permanently delete ALL stock batches matching your current filters."
                    : "This will permanently delete the selected stock batches."}
                </p>
                
                {(() => {
                  const summary = getDeleteSummary();
                  return (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Stock Rows:</span>
                          <span className="ml-2 font-semibold text-destructive">{summary.count}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Total Quantity:</span>
                          <span className="ml-2 font-semibold text-destructive">{summary.quantity}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Products Affected:</span>
                          <span className="ml-2 font-semibold">{summary.productCount}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Shop:</span>
                          <span className="ml-2 font-semibold">{summary.shopName || "Multiple"}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                <p className="text-xs text-red-600 font-medium">
                  ⚠ This action will permanently delete the selected stock entries and cannot be undone.
                  Product master data in Inventory will NOT be affected.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : `Delete ${getDeleteSummary().count} Stock Row${getDeleteSummary().count !== 1 ? "s" : ""}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stock Change Diagnostic Dialog */}
      <StockDiagnosticDialog
        open={diagnosticOpen}
        onOpenChange={setDiagnosticOpen}
        shops={shops}
        selectedShopId={selectedShop}
      />
    </div>
  );
}
